// Client-side expedition-entry + completion state (Phase 5 Step 17).
//
// Pure state module — no PixiJS imports — mirrors party-state.ts. The
// server-message-reducer mutates state via the reducers below; the
// expedition-ready-ui modal renders off the current state each frame.
//
// Two load-bearing fields:
//   - pendingReady: the latest un-acted expeditionReady envelope. Modal
//     opens when non-null; cleared by accept/decline click.
//   - dismissedInvites: set of expeditionIds the user has explicitly
//     declined on *this* client. Persisted to localStorage so the
//     durable expeditionReady re-replayed on reconnect doesn't re-prompt.
//   - completed: latched expeditionCompleted envelope; the completion
//     modal renders until the user clicks Return to Station.
//   - acceptedPending: expeditionId the user just accepted, awaiting
//     the server's expeditionEntered envelope to trigger WS reconnect.
//     Not persisted: if the page reloads between click and envelope
//     the resume handshake replays expeditionEntered and the client
//     rejoins via resume_state.current_expedition_id.

export type PendingReady = {
  expeditionId: string;
  archetype: string;
  leaderId: string;
  leaderName: string;
  receivedAt: number;
};

export type ExpeditionCompletedEntry = {
  expeditionId: string;
  reason: "boss_defeated" | "abandoned";
};

export type ExpeditionClientState = {
  pendingReady: PendingReady | null;
  dismissedInvites: Set<string>;
  completed: ExpeditionCompletedEntry | null;
  acceptedPending: string | null;
};

export const DISMISSED_INVITES_STORAGE_KEY = "nexus-dismissed-invites";

export function createExpeditionState(): ExpeditionClientState {
  return { pendingReady: null, dismissedInvites: new Set(), completed: null, acceptedPending: null };
}

type StorageLike = { getItem(k: string): string | null; setItem(k: string, v: string): void };

// Rehydrate the dismissed-invites set on boot. Silent on parse failure —
// a corrupt row is indistinguishable from "none saved" for our purposes.
export function loadDismissedInvites(storage: StorageLike): Set<string> {
  const raw = storage.getItem(DISMISSED_INVITES_STORAGE_KEY);
  if (raw === null) return new Set();
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((v): v is string => typeof v === "string"));
  } catch {
    // contracts:boundary — corrupt localStorage row is indistinguishable
    // from "none saved"; fall through to empty set.
    return new Set();
  }
}

export function saveDismissedInvites(storage: StorageLike, dismissed: Set<string>): void {
  storage.setItem(DISMISSED_INVITES_STORAGE_KEY, JSON.stringify([...dismissed]));
}

// ---- reducers ----

export function applyExpeditionReady(
  state: ExpeditionClientState,
  payload: { expeditionId: string; archetype: string; leaderId: string },
  now: number,
): void {
  if (state.dismissedInvites.has(payload.expeditionId)) return;
  // Refresh-in-place on duplicate — matches Step 16b's partyInvite idiom.
  if (state.pendingReady?.expeditionId === payload.expeditionId) {
    state.pendingReady.archetype = payload.archetype;
    state.pendingReady.leaderId = payload.leaderId;
    state.pendingReady.leaderName = payload.leaderId;
    state.pendingReady.receivedAt = now;
    return;
  }
  state.pendingReady = {
    expeditionId: payload.expeditionId,
    archetype: payload.archetype,
    leaderId: payload.leaderId,
    leaderName: payload.leaderId,
    receivedAt: now,
  };
}

// expeditionExited / expeditionCompleted both clear a matching
// pendingReady and prune the corresponding dismissedInvites entry so
// a re-invite for the same (restarted) expedition isn't silently eaten.
export function clearForExpedition(
  state: ExpeditionClientState,
  expeditionId: string,
): void {
  if (state.pendingReady?.expeditionId === expeditionId) state.pendingReady = null;
  if (state.acceptedPending === expeditionId) state.acceptedPending = null;
  state.dismissedInvites.delete(expeditionId);
}

export function applyExpeditionCompleted(
  state: ExpeditionClientState,
  payload: { expeditionId: string; reason: "boss_defeated" | "abandoned" },
): void {
  state.completed = { expeditionId: payload.expeditionId, reason: payload.reason };
  clearForExpedition(state, payload.expeditionId);
}

// Called when the Accept click resolves on the server (expeditionEntered
// envelope arrives). Returns true if the arriving envelope matches a
// pending accept — the caller uses the signal to trigger the WS close+
// reconnect that the Worker needs to reroute to ExpeditionDO.
export function consumeAcceptedIfMatch(
  state: ExpeditionClientState,
  expeditionId: string,
): boolean {
  if (state.acceptedPending !== expeditionId) return false;
  state.acceptedPending = null;
  return true;
}

// Phase 7 Block A — playtest finding 8.5 (expedition entry never
// transitions). The server-side `expeditionReadyConfirmed` ephemeral
// is delivered on the live ZoneDO socket and races socket close
// during the cross-DO transition; when it loses, the client sits with
// `acceptedPending` set forever (the durable `expeditionEntered`
// envelope is in the resume buffer but the client never reconnects to
// pick it up). This helper schedules a one-shot fallback: if
// acceptedPending still matches expeditionId after delayMs, force the
// reconnect ourselves. Setting `state.acceptedPending = null` first
// keeps the reducer arm idempotent if the envelope arrives during
// the reconnect itself.
export type AcceptanceFallbackOpts = {
  readonly expeditionId: string;
  readonly state: ExpeditionClientState;
  readonly forceReconnect: () => void;
  readonly setTimer: (cb: () => void, ms: number) => unknown;
  readonly delayMs: number;
};

export function scheduleAcceptanceFallback(opts: AcceptanceFallbackOpts): unknown {
  return opts.setTimer(() => {
    if (opts.state.acceptedPending !== opts.expeditionId) return;
    opts.state.acceptedPending = null;
    opts.forceReconnect();
  }, opts.delayMs);
}
